import React from 'react'
import moment from 'moment'
import 'moment/locale/th'
import MomentUtils from '@date-io/moment'
import { MuiPickersUtilsProvider, TimePicker } from 'material-ui-thai-datepickers'
import { FormControl, FormHelperText, Grid, Typography, InputAdornment } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { AccessTimeOutlined } from '@material-ui/icons'

import useLanguageStore from '../../stores/language/languageStore'
import useTimepicker from './useTimepicker'
import { MyTimePickerProps, TimePickerViewProps } from './timepickerType'

function toMinutes(hhmm?: string) {
    if (!hhmm) return undefined
    const [h, m] = hhmm.split(':').map(Number)
    return (h || 0) * 60 + (m || 0)
}
function sameDayMomentFrom(hhmm?: string) {
    if (!hhmm) return null
    const [h, m] = hhmm.split(':').map(Number)
    const base = moment().startOf('day')
    return base
        .clone()
        .hour(h || 0)
        .minute(m || 0)
        .second(0)
        .millisecond(0)
}

const MyTimePickerView: React.FC<TimePickerViewProps> = ({
    onChangeValue,
    required,
    label,
    field,
    disabled,
    clearable = true,
    icon = <AccessTimeOutlined />,
    size = 'small',
    range,
    minuteStep = 15,
}) => {
    const { input, meta } = field
    const isError = meta.error && meta.invalid && meta.touched ? true : false
    const classes = useStyles(isError)
    const languageStore = useLanguageStore()

    React.useEffect(() => {
        moment.locale(languageStore.currentLanguage === 'th' ? 'th' : 'en')
    }, [languageStore.currentLanguage])

    const valueMoment = input.value ? sameDayMomentFrom(input.value) : null
    const minM = toMinutes(range?.start)
    const maxM = toMinutes(range?.end)

    const isOutOfRange = (m: moment.Moment) => {
        const total = m.hours() * 60 + m.minutes()
        if (minM != null && total < minM) return true
        if (maxM != null && total > maxM) return true
        return false
    }

    const getLabel = () => {
        const star =
            (typeof required === 'boolean' && required) || (typeof required === 'object' && required?.triggedValidate)
        return (
            <Typography variant="subtitle2">
                <Grid container className={classes.label}>
                    <Grid item>
                        <Typography variant="subtitle2">{label || ''}</Typography>
                    </Grid>
                    {star && (
                        <Grid item>
                            <Typography variant="subtitle2" style={{ color: 'red' }}>
                                *
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Typography>
        )
    }

    return (
        <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale={languageStore.currentLanguage}>
            <FormControl style={{ height: '108px', width: '100%' }}>
                {getLabel()}

                <TimePicker
                    value={valueMoment}
                    onChange={(newMoment) => {
                        if (!newMoment || !newMoment.isValid()) {
                            input.onChange(undefined)
                            onChangeValue?.(undefined)
                            return
                        }

                        let m = newMoment.clone().second(0).millisecond(0)

                        if (isOutOfRange(m)) {
                            if (minM != null && maxM != null) {
                                const total = m.hours() * 60 + m.minutes()
                                const pickMin = Math.abs(total - minM) <= Math.abs(total - maxM)
                                m = pickMin ? sameDayMomentFrom(range?.start)! : sameDayMomentFrom(range?.end)!
                            } else if (minM != null) {
                                m = sameDayMomentFrom(range?.start)!
                            } else if (maxM != null) {
                                m = sameDayMomentFrom(range?.end)!
                            }
                        }

                        const mins = m.minutes()
                        const snapped = Math.round(mins / minuteStep) * minuteStep
                        m.minutes(snapped % 60)

                        const val = m.format('HH:mm')
                        input.onChange(val)
                        onChangeValue?.(val)
                    }}
                    onClose={input.onBlur}
                    ampm={false}
                    minutesStep={minuteStep}
                    inputVariant="outlined"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end" className={classes.endAdornment}>
                                {icon}
                            </InputAdornment>
                        ),
                    }}
                    size={size}
                    disabled={disabled}
                    error={isError}
                    className={`${classes.disable} ${classes.inputRoot}`}
                    clearable={clearable}
                />

                <FormHelperText className={classes.errorText}>{isError && meta.error}</FormHelperText>
            </FormControl>
        </MuiPickersUtilsProvider>
    )
}

const MyTimePicker: React.FC<MyTimePickerProps> = ({ required, name, ...others }) => {
    const myTimePicker = useTimepicker({ required, name })
    return <MyTimePickerView {...myTimePicker} {...others} required={required} />
}

export default MyTimePicker

const useStyles = makeStyles(() => ({
    label: {
        marginBottom: 10,
        color: (isError: any) => (isError ? '#ff4d4f' : ''),
    },
    errorText: {
        fontSize: '12px',
        color: '#ff4d4f',
    },
    disable: {
        '& .Mui-disabled': {
            color: '#000000',
            backgroundColor: '#F0F0F0C6',
        },
    },

    inputRoot: {
        '& .MuiOutlinedInput-root.MuiOutlinedInput-adornedEnd .MuiOutlinedInput-input': {
            paddingRight: 40,
        },
    },

    endAdornment: {
        pointerEvents: 'none',
        color: 'rgba(0, 0, 0, 0.26)',
    },
}))
